import React, {useContext} from 'react';
import {useFormContext, useWatch} from 'react-hook-form';
import {Label, LabelProps} from 'reactstrap';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import {ConditionalStatusField} from '../../ConditionalStatusField';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import FieldLayout from './FieldLayout';
import {IOrderProductStatusModel} from '@src/@interfaces/models/Order/IOrderProductStatusModel';
import {StatusCommentField} from './StatusCommentField';
import app from '@src/helpers/app';
import {UserContext} from '@src/contexts/UserContext';
import StatusJudgeApproval from './StatusJudgeApproval';
import Translate from '@src/components/Translate/Translate';
import {IOrderProductModel} from '@src/@interfaces/models/Order/IOrderProductModel';
import OrderStatusRepresentation from '@src/components/StatusRepresentations/OrderStatusRepresentation';
import usePermissions from '@src/hooks/usePermissions';

const emptyArray: never[] = [];

export function StatusBadge(props: {
    rowIndex: number,
    className?: string,
}) {
    const {
        control, fieldName, modelOptions = {}
    } = useContext<IFormFieldItemPropsContext<IOrderModel> | null>(FormFieldItemPropsContext) ?? {};
    const nestedFieldName = `${fieldName}.${props.rowIndex}.status_id`;

    const activeStatusId = useWatch({control, name: nestedFieldName});
    const options = modelOptions.productStatuses ?? emptyArray;
    const activeStatus = options?.find(({value}) => value === activeStatusId) as any as (IOrderProductStatusModel | null);

    if (!activeStatus) {
        return null;
    }

    return <OrderStatusRepresentation
            tag="div"
            className={props.className}
            label={activeStatus.title}
            bgColor={activeStatus.bg_color}
            textColor={activeStatus.text_color}
    />;
}

export function StatusField({rowIndex: index, isDisabled}: { rowIndex: number, isDisabled?: boolean }) {
    const {
        control,
        fieldName,
        formName,
        modelOptions = {},
        model,
        forceDisabled,
        forceReadonly
    } = useContext<IFormFieldItemPropsContext<IOrderModel> | null>(FormFieldItemPropsContext) ?? {};

    const nestedFieldName = `${fieldName}.${index}.status_id`;

    const userContext = useContext(UserContext);
    const {can} = usePermissions();
    const {formState: {dirtyFields}} = useFormContext();
    const activeStatusId = useWatch({control, name: nestedFieldName});
    const options = modelOptions.productStatuses ?? emptyArray;
    const activeStatus = options?.find(({value}) => value === activeStatusId);
    const statusRequiresReason = ((activeStatus as any) as IOrderProductStatusModel)?.comment_is_required === true;

    if (!control || !formName || !fieldName || !userContext) {
        return null;
    }

    const dot = app('dot-accessor');
    const isProductionMan = can('users@is-production');
    const canLeaveReason = can('orders@can-leave-order-product-status-changing-reason');
    const isDirty = dot.get(dirtyFields, nestedFieldName, false);
    const shouldDisplayReasonForm = statusRequiresReason && isProductionMan && canLeaveReason && isDirty;

    const orderId = model?.id;
    const currentProduct: IOrderProductModel | null = dot.get(model ?? {}, `${fieldName}.${index}`);
    const approvalRequired = (
            activeStatusId === currentProduct?.status_id &&
            currentProduct?.status_approval_state === 'required'
    );
    const isResponsibleManager = Boolean(userContext.model?.id) && userContext.model?.id === model?.manager_id;
    const canManageApproval = (
            isResponsibleManager && can(
                    'orders@can-responsible-manager-judge-order-product-status-changing-approval'
            ) ||
            can('orders@can-judge-order-product-status-changing-approval')
    );
    const shouldDisplayManageApprovalForm = approvalRequired && canManageApproval;

    return <FieldLayout
            id={null}
            label="OrderForm.products.fields.status"
            className="mb-0"
    >
        <ConditionalStatusField
                control={control}
                fieldName={nestedFieldName}
                fieldLabel="OrderForm.products.fields.status"
                options={options}
                forceReadonly={forceReadonly}
                forceDisabled={isDisabled || forceDisabled}
                approvalRequired={approvalRequired}
        />

        {(orderId && currentProduct?.id && shouldDisplayManageApprovalForm) ? <>
            <Translate<HTMLLabelElement, LabelProps>
                    tag={Label}
                    message="OrderForm.products.status_approval.approval_required"
                    size="sm"
            />

            <StatusJudgeApproval
                    orderId={orderId}
                    orderProductId={currentProduct.id}
                    controllerName={formName}
                    placeholder="CommentsList.message"
                    isDisabled={isDisabled || forceDisabled}
            />
        </> : null}

        {shouldDisplayReasonForm ? (
                <div className="mt-2">
                    <StatusCommentField
                            control={control}
                            containerPath={`${fieldName}.${index}.status_changing_reason`}
                            isDisabled={isDisabled || forceDisabled}
                            isReadonly={forceReadonly}
                    />
                </div>
        ) : null}
    </FieldLayout>;
}
